import { message } from 'antd';
import uniqBy from 'lodash/uniqBy';
import { put, select, takeLatest } from 'redux-saga/effects';
import { SpaceTabDocumentType } from 'api/space/space-tab';
import { logger } from 'services';
import { floatUid } from 'utils/uid';
import { MentionsDataParam } from 'vendor/ckeditor/plugins/mention/constants';
import { setupSpaceInProjectTemplateAction } from '../../actions';
import { hasSetupSpacesInTemplate, selectAllTemplateTask } from '../../selectors';
import { setupSpacesOnProjectTemplate, setupSpacesOnProjectTemplateComplete, setupSpacesOnProjectTemplateInProgress } from '../../slices';
const getAllProjectPhasesSelector = ()=>({ templates })=>templates.project.data[templates.project.activeId].projectPhases;
const scrapDocumentTemplatesFromTaskDescription = (taskDescription)=>{
    const $parsed = new DOMParser().parseFromString(taskDescription, 'text/html').documentElement;
    const spaceTabs = [];
    $parsed.querySelectorAll(`[${MentionsDataParam.Type}="DOCUMENT"]`).forEach((node)=>{
        const spaceId = node.getAttribute(MentionsDataParam.ObjectId);
        const spaceTabTitle = node.getAttribute('data-mention') || '';
        spaceTabs.push({
            spaceTabId: spaceId != null ? parseInt(spaceId, 10) : floatUid(),
            spaceTabType: SpaceTabDocumentType.RocketlaneDocument,
            spaceTabTitle,
            url: `/templates/${spaceId}`
        });
    });
    return spaceTabs;
};
const findRecursiveChildNodesForDocuments = (spaceTabs, allTasks, tasks)=>{
    tasks.forEach((taskId)=>{
        const task = allTasks[taskId];
        if (task.taskDescription) {
            spaceTabs.push(scrapDocumentTemplatesFromTaskDescription(task.taskDescription));
        }
        if (task.subTasks) {
            findRecursiveChildNodesForDocuments(spaceTabs, allTasks, task.subTasks);
        }
    });
    return spaceTabs;
};
const spaceTabIds = [];
const extractDocumentFromPhaseTasks = (allTasks, phase)=>{
    const { tasks } = phase;
    const spaceTabs = [];
    findRecursiveChildNodesForDocuments(spaceTabs, allTasks, tasks);
    const uniqSpaceTabs = uniqBy(spaceTabs.flat(), 'spaceTabId');
    const redundanctSpaceTabs = [];
    uniqSpaceTabs.forEach((spaceTab)=>{
        if (!spaceTabIds.includes(spaceTab.url)) {
            redundanctSpaceTabs.push(spaceTab);
            spaceTabIds.push(spaceTab.url);
        }
    });
    return redundanctSpaceTabs;
};
function* setupSpacesForProjectTemplate(action) {
    const { payload: { privateSpaceName, sharedSpaceName, handleFailure } } = action;
    try {
        const allTasks = yield select(selectAllTemplateTask());
        const spaceSetupState = yield select(hasSetupSpacesInTemplate());
        if (spaceSetupState === 'COMPLETE') {
            return;
        }
        if (spaceSetupState === 'YET_TO_START') {
            yield put(setupSpacesOnProjectTemplateInProgress());
        }
        const _phases = yield select(getAllProjectPhasesSelector());
        const phases = Object.values(_phases);
        const privatePhases = phases.filter((phase)=>phase.private);
        const sharedPhases = phases.filter((phase)=>!phase.private);
        const privateSpaceTabs = privatePhases.reduce((spaceTabs, phase)=>{
            spaceTabs.push(...extractDocumentFromPhaseTasks(allTasks, phase));
            return spaceTabs;
        }, []);
        const sharedSpaceTabs = sharedPhases.reduce((spaceTabs, phase)=>{
            spaceTabs.push(...extractDocumentFromPhaseTasks(allTasks, phase));
            return spaceTabs;
        }, []);
        const privateSpaceId = floatUid();
        const sharedSpaceId = floatUid();
        const spaceList = {
            [privateSpaceId]: {
                spaceId: privateSpaceId,
                spaceName: privateSpaceName,
                spaceTabs: privateSpaceTabs,
                private: true
            },
            [sharedSpaceId]: {
                spaceId: sharedSpaceId,
                spaceName: sharedSpaceName,
                spaceTabs: sharedSpaceTabs,
                private: false
            }
        };
        yield put(setupSpacesOnProjectTemplate(spaceList));
        yield put(setupSpacesOnProjectTemplateComplete());
    } catch (error) {
        logger.error('There was an error setting up spaces.', {
            error
        });
        message.error('There was an error setting up spaces. Please try later');
        if (handleFailure) {
            handleFailure();
        }
    }
}
export function* watchsetupSpacesForProjectTemplateSaga() {
    yield takeLatest(setupSpaceInProjectTemplateAction.type, setupSpacesForProjectTemplate);
}
